// The shapes the web app works with. They follow biblestandard-api's
// docs/fellowship/05-BACKEND-API.md §3, so the real API can replace the prototype data without the
// screens changing. Times are ISO-8601 strings, as the API returns them.

export type UserCard = { id: string; handle: string; displayName: string; avatarUrl: string | null };

export const REACTIONS = ["amen", "praying", "love", "insight"] as const;
export type Reaction = (typeof REACTIONS)[number];

export const REACTION_LABEL: Record<Reaction, string> = {
  amen: "Amen",
  praying: "Praying",
  love: "Love",
  insight: "Insight",
};

/** "milestone" is a Phase 2 preview: progress the reader chose to share ("finished reading Romans"). */
export type PostKind = "verse" | "highlight" | "note" | "votd_repost" | "milestone";
export type Visibility = "friends" | "private";

/** A photo on a post (FW-007): private, served by the API only to who may see the post. */
export type PostImage = { id: string; url: string; alt: string; width: number; height: number; contentType: "image/webp" | "image/jpeg" | "image/png" };
/** An uploaded photo not yet on a post (POST /v1/post-images): yours alone, deleted unused after 24 hours. */
export type ImageUpload = Omit<PostImage, "alt"> & { bytes: number; expiresAt: string };
/** A photo to attach when posting: an upload's id and what it shows. */
export type NewImage = { id: string; alt: string };

export type Post = {
  id: string;
  author: UserCard;
  kind: PostKind;
  visibility: Visibility;
  body: string | null;
  title: string | null;
  ref: { key: string; keyVersion: number; label: string } | null;
  translation: string | null;
  quote: string | null;
  work: { id: string; label: string; locator: string } | null;
  /** A highlight's place in the app's library (F7): the section it was taken from, as a reference. */
  source?: { libraryId: string; sectionUid: string } | null;
  votdKey: string | null;
  reactions: Record<Reaction, number>;
  myReaction: Reaction | null;
  commentCount: number;
  saved: boolean;
  createdAt: string;
  editedAt: string | null;
  canEdit: boolean;
  canDelete: boolean;
  /** Up to four photos, in order (FW-007); an empty list on most posts. */
  images: PostImage[];
};

export type Comment = { id: string; postId: string; author: UserCard; body: string; createdAt: string; canDelete: boolean };

export type Relation = "self" | "friends" | "requested" | "incoming" | "none";

export type Profile = UserCard & {
  bio: string | null;
  friendCount: number;
  relation: Relation;
  /** FS-136: only what the owner shows this viewer (each null otherwise). */
  details?: ProfileDetails;
};

// ---- FS-136 profile details (API: PUT /v1/me/profile, GET /v1/profile-options) ----

/** Optional, self-declared, never inferred. Location is shown to friends unless private; faith only when shared. */
export type ProfileDetails = {
  /** A city or region in the reader's own words (never a coordinate). */
  city: string | null;
  /** ISO 3166-1 alpha-2, e.g. "GB". */
  country: string | null;
  /** A key of ProfileOptions.traditions. */
  tradition: string | null;
  /** The reader's own words, e.g. "PCA". */
  denomination: string | null;
  church: string | null;
};
export type DetailVisibility = "friends" | "private";
/** Your own details and who sees them (Session.profile). */
export type ProfileSettings = { details: ProfileDetails; locationVisibility: DetailVisibility; faithVisibility: DetailVisibility; faithConsentAt: string | null };
/** A change to your details: left out keeps a value, null clears it. faithConsent: true once the reader agreed to faithConsentText. */
export type ProfileDetailsInput = Partial<ProfileDetails> & { locationVisibility?: DetailVisibility; faithVisibility?: DetailVisibility; faithConsent?: boolean };
export type ProfileOptions = {
  traditions: { key: string; label: string }[];
  visibilities: DetailVisibility[];
  defaults: { locationVisibility: DetailVisibility; faithVisibility: DetailVisibility };
  limits: { city: number; denomination: number; church: number };
  /** Shown before the faith fields are kept (GDPR Art. 9); the reader must agree. */
  faithConsentText: string;
};
export const NO_DETAILS: ProfileDetails = { city: null, country: null, tradition: null, denomination: null, church: null };

export type Votd = {
  key: string; // e.g. "2026-10-09"
  ref: { key: string; label: string };
  translation: string;
  quote: string;
  counts: { likes: number; shares: number; reposts: number };
  liked: boolean;
  /** One applied sentence, first person plural (after Ligonier's Coram Deo). */
  reflection: string | null;
  /** Passages to read alongside, as references. */
  alongside: string[];
};

export type Notification = {
  id: string;
  kind: "friend_request" | "friend_accepted" | "reaction" | "comment" | "mention" | "report_outcome" | "plan_invite";
  /** Null for news from Bible Standard itself (a report's outcome). */
  actor: UserCard | null;
  others: number;
  postId: string | null;
  /** A plan_invite's journey (answered on the Plans page). */
  journeyId?: string | null;
  excerpt: string | null;
  /** Plain words from Bible Standard (a report's outcome), or a plan_invite's plan title. */
  detail?: string | null;
  /** A reaction notification: the response given (the latest, when several are grouped). */
  reaction?: Reaction | null;
  createdAt: string;
  read: boolean;
};

export type Passage = {
  key: string;
  label: string;
  translation: string;
  text: string;
  /** A whole chapter or more (a reading plan's day), verse by verse, for reading rather than quoting. */
  chapters?: { n: number; verses: { n: number; text: string }[] }[];
};

export type Friends = { friends: UserCard[]; incoming: UserCard[]; outgoing: UserCard[] };

/** PRD P1/P3/R2 limits, mirrored by the API's Zod schemas and SQL CHECKs. */
export const LIMITS = { reflection: 2000, note: 5000, quote: 1000, comment: 1000, bio: 160, name: 50, images: 4, alt: 500, imageBytes: 4 * 1024 * 1024 } as const;

/** What a post is made from. Any kind may carry up to four photos, each with alt text (FW-007). */
export type NewPost = (
  | { kind: "verse"; refKey: string; translation: string; quote?: string; body: string; visibility: Visibility }
  | { kind: "note"; title: string; body: string; refKey?: string; visibility: Visibility }
  /** A passage from a book in the library: `work` names it for display ("Author · Title", the section), `source` is where it is. */
  | { kind: "highlight"; work: { id: string; label: string; locator: string }; source: { libraryId: string; sectionUid: string }; quote: string; body: string; visibility: Visibility }
  | { kind: "votd_repost"; body: string; visibility: Visibility }
  | { kind: "milestone"; title: string; body: string; visibility: Visibility }
) & { images?: NewImage[] };

export type Switch = { state: "on" | "off" | "maintenance"; message?: string };
/** The API's switches (GET /v1/config): Fellowship (social), sync, web purchases, and the rights switches. */
export type Config = { social: Switch; features?: Record<string, Switch> };

/** Which sign-in providers this server can offer on the web (W1; 503 provider-not-configured otherwise). */
export type AuthProviders = { google: boolean; apple: boolean };
export type SocialProvider = "google" | "apple";
/** A way to sign in, as GET /v1/me/accounts lists it. */
export type SignInMethod = { provider: "email" | SocialProvider; email: string | null; linkedAt: string };
/** A signed-in browser or device, as GET /v1/me/sessions lists it. */
export type Device = { id: string; device: string | null; createdAt: string; lastUsedAt: string; expiresAt: string; current: boolean };
